"use client";

import { signIn, signOut, useSession } from "next-auth/react";
import { Suspense, useEffect } from "react";
import { useSearchParams, useRouter } from "next/navigation";
import { useI18n } from "@/lib/i18n";

function LoginContent() {
  const { status } = useSession();
  const params = useSearchParams();
  const router = useRouter();
  const { t } = useI18n();

  const next = params.get("next") || "/";
  const reason = params.get("reason");

  const sessionMessage =
    reason === "timeout"
      ? t("login.sessionTimeout")
      : reason === "refresh-failed" || reason === "unauthorized"
        ? t("login.sessionExpired")
        : null;

  useEffect(() => {
    if (status === "authenticated" && !reason) {
      router.replace(next);
    }
  }, [status, next, reason, router]);

  useEffect(() => {
    if (reason && status === "authenticated") {
      signOut({ redirect: false });
    }
  }, [reason, status]);

  return (
    <div className="min-h-screen flex items-center justify-center bg-neutral-950 text-white px-6">
      <div className="w-full max-w-md bg-neutral-900 border border-neutral-800 rounded-2xl p-8 shadow-xl text-center">

        <h1 className="text-2xl font-semibold mb-2">
          Nordlicht Hub
        </h1>

        <p className="text-sm text-neutral-400 mb-8">
          {t("login.subtitle")}
        </p>

        {sessionMessage && (
          <div className="mb-6 rounded-lg border border-amber-500/50 bg-amber-500/10 px-4 py-3 text-left text-sm text-amber-200">
            {sessionMessage}
          </div>
        )}

        {status === "loading" && (
          <div className="text-neutral-400 text-sm mb-6">
            {t("login.checking")}
          </div>
        )}

        {status !== "authenticated" && (
          <button
            onClick={() => signIn("authentik", { callbackUrl: next })}
            className="w-full py-3 rounded-xl bg-indigo-600 hover:bg-indigo-500 transition font-medium"
          >
            {t("login.signIn")}
          </button>
        )}

        <div className="mt-6 text-xs text-neutral-500">
          {t("login.oidc")}
        </div>
      </div>
    </div>
  );
}

export default function Login() {
  const { t } = useI18n();

  return (
    <Suspense
      fallback={
        <div className="min-h-screen flex items-center justify-center bg-neutral-950 text-neutral-400">
          {t("login.checkingSession")}
        </div>
      }
    >
      <LoginContent />
    </Suspense>
  );
}
